"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const apiUtils_1 = require("../util/apiUtils");
const auth_1 = require("../util/auth");
const auth0_react_1 = require("@auth0/auth0-react");
const fetchData = async (fetchUrl, signal, body, headersWithAuth) => {
    if (fetchUrl) {
        try {
            const result = await fetch((0, apiUtils_1.getAPIUrl)() + fetchUrl, {
                signal,
                method: body ? "POST" : "GET",
                body,
                headers: headersWithAuth,
            });
            if (!signal.aborted) {
                const data = await result.json();
                if (data.error) {
                    return { status: apiUtils_1.LoaderStatus.Error, error: data.error };
                }
                else {
                    return { status: apiUtils_1.LoaderStatus.Success, data };
                }
            }
        }
        catch (error) {
            if (signal.aborted) {
                console.log("Fetch aborted:", error);
            }
            else {
                console.error(error);
                return { status: apiUtils_1.LoaderStatus.Error, error };
            }
        }
    }
    return {
        status: apiUtils_1.LoaderStatus.Idle,
    };
};
const fetchAllData = async (setResponse, fetchUrl, signal, getAuthHeader, getAccessTokenSilently, loginWithRedirect, bodies) => {
    if (fetchUrl) {
        setResponse({ status: apiUtils_1.LoaderStatus.Loading });
        try {
            const accessToken = await (0, auth_1.getAccessToken)(getAccessTokenSilently); // Fetch the token
            const authHeader = getAuthHeader(accessToken);
            const headersWithAuth = {
                "Content-Type": "application/json",
                ...authHeader,
            }; // Pass the token to getAuthHeader
            const fetchPromises = bodies.map((body) => fetchData(fetchUrl, signal, body, headersWithAuth));
            Promise.all(fetchPromises)
                .then((results) => {
                const hasAuthFailure = results.some((result) => result.status === apiUtils_1.LoaderStatus.Error &&
                    (0, auth_1.shouldTriggerLoginRedirect)(result.error));
                if (hasAuthFailure) {
                    loginWithRedirect();
                    return;
                }
                setResponse({
                    status: apiUtils_1.LoaderStatus.Success,
                    data: results.map((r) => r.status === apiUtils_1.LoaderStatus.Success ? r.data : undefined),
                });
            })
                .catch((error) => {
                console.error("Failed to fetch some candidate records:", error);
                return {
                    status: apiUtils_1.LoaderStatus.Error,
                };
            });
        }
        catch (error) {
            if (signal.aborted) {
                console.log("Fetch aborted:", error);
            }
            else if ((0, auth_1.shouldTriggerLoginRedirect)(error)) {
                loginWithRedirect();
            }
            else {
                console.error(error);
                setResponse({ status: apiUtils_1.LoaderStatus.Error, error });
            }
        }
    }
};
const useFetchAll = (initialUrl, initialBody, initialBodyVariations) => {
    const { getAuthHeader, getAccessTokenSilently } = (0, auth_1.useAuthentication)();
    const { loginWithRedirect } = (0, auth0_react_1.useAuth0)();
    const [url, setUrl] = (0, react_1.useState)(initialUrl);
    const [postBody, setPostBody] = (0, react_1.useState)(initialBody);
    const [postBodyVariations, setPostBodyVariations] = (0, react_1.useState)(initialBodyVariations);
    const [response, setResponse] = (0, react_1.useState)({
        status: apiUtils_1.LoaderStatus.Idle,
    });
    (0, react_1.useEffect)(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        if (url && postBody && postBodyVariations) {
            if (postBodyVariations) {
                const bodies = postBodyVariations?.variations.map((variation) => {
                    return JSON.stringify({
                        ...(postBody ?? initialBody),
                        [postBodyVariations.key]: variation,
                    });
                });
                fetchAllData(setResponse, url, signal, getAuthHeader, getAccessTokenSilently, loginWithRedirect, bodies);
            }
        }
        return () => {
            if (!signal.aborted) {
                abortController.abort(); // Abort only if the signal hasn't already been aborted
            }
        };
    }, [url, postBody, postBodyVariations]);
    const refresh = () => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        if (url && postBody && postBodyVariations) {
            if (postBodyVariations) {
                const bodies = postBodyVariations?.variations.map((variation) => {
                    return JSON.stringify({
                        ...(postBody ?? initialBody),
                        [postBodyVariations.key]: variation,
                    });
                });
                fetchAllData(setResponse, url, signal, getAuthHeader, getAccessTokenSilently, loginWithRedirect, bodies);
            }
        }
    };
    if (!initialUrl && !url) {
        return [
            { status: apiUtils_1.LoaderStatus.Idle },
            setUrl,
            setPostBody,
            setPostBodyVariations,
            refresh,
        ];
    }
    return [response, setUrl, setPostBody, setPostBodyVariations, refresh];
};
exports.default = useFetchAll;
